import {
  Input,
  Output,
  Component,
  OnDestroy,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnFloor } from '@lib/api/entities';
import { t } from '@lib/translation';
import { FloorAlertState } from '../../services/floor-alert-state.service';

@Component({
  selector: 'sn-floor-range',
  templateUrl: './floor-range.component.html',
  styleUrls: ['./floor-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnFloorRangeComponent implements OnDestroy {
  @Input()
  public floors: SnFloor[] = [];

  @Input()
  public floorAlertStates?: Map<string, FloorAlertState> | null;

  constructor() {}

  @Input()
  get activeFloor(): SnFloor {
    return this._activeFloor;
  }
  set activeFloor(value: SnFloor) {
    const index = this.floors.indexOf(value);

    if (index > -1) {
      this._activeFloor = value;
      this._sliderValue = index + 1;
    } else {
      this._activeFloor = undefined;
      this._sliderValue = 0;
    }
  }

  @Output()
  public readonly floorChanged = new EventEmitter<SnFloor>();

  public readonly getLabel = this._getLabel.bind(this);
  public readonly getTranslatedLabel = this._getTranslatedLabel.bind(this);

  private _activeFloor: SnFloor;
  private _sliderValue: number;

  get sliderValue(): number {
    return this._sliderValue;
  }

  public ngOnDestroy(): void {
    this.floorChanged.complete();
  }

  public trackFloor(index: number, item: SnFloor): string {
    return item.id;
  }

  public changeValue(floorId?: string): void {
    this._sliderValue = floorId
      ? this.floors.findIndex((floor: SnFloor) => floor.id === floorId) + 1
      : 0;
    this.onInput(this._sliderValue);
    this.onChange();
  }

  public onInput(value: number): void {
    this.activeFloor = this.floors[value - 1];
  }

  public onChange(): void {
    this.floorChanged.next(this.activeFloor);
  }

  /**
   * Gets the alert state for a floor at the given index.
   * Returns null if no alerts exist for this floor.
   */
  public getFloorAlertState(index: number): FloorAlertState | null {
    if (!this.floorAlertStates || !this.floors[index]) {
      return null;
    }

    const floor = this.floors[index];
    const state = this.floorAlertStates.get(floor.id);

    if (!state) {
      return null;
    }

    // Only return state if there are actual alerts
    if (state.criticalAlertCount > 0 || state.warningAlertCount > 0) {
      return state;
    }

    return null;
  }

  /**
   * Gets the alert severity for a floor alert state.
   * Returns 'critical' if any critical alerts exist, 'warning' if only warning alerts exist.
   */
  public getAlertSeverity(state: FloorAlertState): 'critical' | 'warning' {
    return state.criticalAlertCount > 0 ? 'critical' : 'warning';
  }

  /**
   * Calculates the vertical position for a floor's alert indicator.
   * The slider has N+1 tick marks: position 0 for "ALL" + N positions for each floor.
   *
   * @param floorIndex 0-based index of the floor in the floors array
   * @returns CSS bottom position as percentage string
   *
   * @example
   * // For 3 floors: positions are 0 (ALL), 1 (Floor 1), 2 (Floor 2), 3 (Floor 3)
   * getAlertIndicatorPosition(0) // Returns "33.33%" (Floor 1 at position 1 out of 0-3)
   * getAlertIndicatorPosition(1) // Returns "66.67%" (Floor 2 at position 2 out of 0-3)
   * getAlertIndicatorPosition(2) // Returns "100%"  (Floor 3 at position 3 out of 0-3)
   */
  public getAlertIndicatorPosition(floorIndex: number): string {
    const totalTicks = this.floors.length + 1; // +1 for "ALL" position at bottom
    const tickPosition = floorIndex + 1; // Skip position 0 (reserved for "ALL")
    const percentage = (tickPosition / (totalTicks - 1)) * 100;
    return `${percentage}%`;
  }

  /**
   * Gets the aria-label for a floor alert indicator.
   */
  public getFloorAlertAriaLabel(floor: SnFloor, state: FloorAlertState): string {
    const hasBoth = state.criticalAlertCount > 0 && state.warningAlertCount > 0;
    const hasCritical = state.criticalAlertCount > 0;

    if (hasBoth) {
      return t('map.locations.floorAlerts.bothAriaLabel', {
        floorName: floor.name,
        criticalCount: state.criticalAlertCount,
        warningCount: state.warningAlertCount,
      });
    }

    const key = hasCritical
      ? 'map.locations.floorAlerts.criticalAriaLabel'
      : 'map.locations.floorAlerts.warningAriaLabel';
    const count = hasCritical ? state.criticalAlertCount : state.warningAlertCount;
    return t(key, { floorName: floor.name, count });
  }

  /**
   * Gets the title tooltip for a floor alert indicator.
   */
  public getFloorAlertTitle(floor: SnFloor, state: FloorAlertState): string {
    const hasBoth = state.criticalAlertCount > 0 && state.warningAlertCount > 0;
    const hasCritical = state.criticalAlertCount > 0;

    if (hasBoth) {
      return t('map.locations.floorAlerts.bothTitle', {
        floorName: floor.name,
        criticalCount: state.criticalAlertCount,
        warningCount: state.warningAlertCount,
      });
    }

    const key = hasCritical
      ? 'map.locations.floorAlerts.criticalTitle'
      : 'map.locations.floorAlerts.warningTitle';
    const count = hasCritical ? state.criticalAlertCount : state.warningAlertCount;
    return t(key, { floorName: floor.name, count });
  }

  private _getLabel(value: number): string {
    return value ? this.floors[value - 1]?.name : 'ALL';
  }

  private _getTranslatedLabel(value: number): string {
    return value ? this.floors[value - 1]?.name : t('common.all');
  }
}
